import React, { createContext, useState } from 'react';

import {
  Article,
  GetArticlesParams,
  Order,
  Pagination as IPagination,
  PostOrderBy,
} from '../../client/interfaces';

interface ArticlesParamsContextType {
  getArticlesParams: GetArticlesParams;
  setGetArticlesParams: React.Dispatch<React.SetStateAction<GetArticlesParams>>;
  nextPageIdentifier: number;
  setNextPageIdentifier: React.Dispatch<React.SetStateAction<number>>;
  allArticles: Article[];
  setAllArticles: React.Dispatch<React.SetStateAction<Article[]>>;
  resetInfiniteScroll: () => void;
  emptyGetArticlesParams: GetArticlesParams;
  emptyArticlesFilter: () => void;
  articlePagination: IPagination;
  setArticlePagination: React.Dispatch<React.SetStateAction<IPagination>>;
  resetArticlePagination: () => void;
}

const emptyGetArticlesParams: GetArticlesParams = {
  orderBy: PostOrderBy.publishedAtUtc,
  order: Order.DESC,
  page: 1,
  perPage: 9,
  search: '',
  tags: [],
  isBookmarked: false,
};

const emptyPagination: IPagination = {
  total: 0,
  perPage: 0,
  currentPage: 1,
  pageCount: 1,
  fromNumber: 0,
  toNumber: 0,
};

const ArticlesParamsContext = createContext<ArticlesParamsContextType>({
  getArticlesParams: emptyGetArticlesParams,
  setGetArticlesParams: () => {},
  nextPageIdentifier: 0,
  setNextPageIdentifier: () => {},
  allArticles: [],
  setAllArticles: () => {},
  resetInfiniteScroll: () => {},
  emptyGetArticlesParams,
  emptyArticlesFilter: () => {},
  articlePagination: emptyPagination,
  setArticlePagination: () => {},
  resetArticlePagination: () => {},
});

export const ArticlesProvider = ({ children }) => {
  const [getArticlesParams, setGetArticlesParams] = useState({ ...emptyGetArticlesParams });
  const [nextPageIdentifier, setNextPageIdentifier] = useState<number>(0);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [articlePagination, setArticlePagination] = useState<IPagination>(emptyPagination);

  const resetInfiniteScroll = () => {
    setNextPageIdentifier(0);
  };

  const resetArticlePagination = () => {
    setArticlePagination(emptyPagination);
  };

  function emptyArticlesFilter() {
    setGetArticlesParams((prevState) => ({
      ...prevState,
      tags: [],
      isBookmarked: false,
      search: '',
    }));
  }

  return (
    <ArticlesParamsContext.Provider
      value={{
        emptyGetArticlesParams,
        getArticlesParams,
        setGetArticlesParams,
        nextPageIdentifier,
        setNextPageIdentifier,
        allArticles,
        setAllArticles,
        resetInfiniteScroll,
        emptyArticlesFilter,
        articlePagination,
        setArticlePagination,
        resetArticlePagination,
      }}
    >
      {children}
    </ArticlesParamsContext.Provider>
  );
};

export { ArticlesParamsContext };
